import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaCheck, FaImage, FaFile, FaExternalLinkAlt, FaCode, FaQuoteRight, FaGripVertical, FaTable, FaEdit, FaTrash, FaPlus, FaMinus, FaSave, FaTimes, FaHeading, FaListUl, FaListOl, FaRegSquare, FaRegCircle, FaMinus as FaMinusIcon, FaLightbulb, FaLink, FaUpload, FaGlobe } from 'react-icons/fa';
import { useDrag, useDrop } from 'react-dnd';
import { BLOCK_TYPES, formatText } from '../utils/blockTypes';
import { uploadApi } from '../services/api'; // Make sure you have this API service
import '../styles/BlockComponent.css';

const ItemTypes = {
  BLOCK: 'block',
};

// Custom ContentEditable component with cursor preservation
const StableContentEditable = React.memo(({
  value,
  onChange,
  placeholder,
  className,
  isEditing,
  format,
  onKeyDown,
  onBlur,
  onClick,
  isReadOnly,
  style,
  ...props
}) => {
  const ref = useRef(null);
  const lastHtml = useRef('');
  const isComposingRef = useRef(false);
  const shouldUpdateRef = useRef(true);

  // Save cursor position
  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount || !ref.current) return null;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(ref.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    return {
      start: preCaretRange.toString().length,
      startContainer: range.startContainer,
      startOffset: range.startOffset,
      endContainer: range.endContainer,
      endOffset: range.endOffset
    };
  };

  // Restore cursor position
  const restoreCursorPosition = (pos) => {
    if (!pos || !ref.current) return;

    const selection = window.getSelection();
    const range = document.createRange();

    try {
      // Try to restore exact position
      if (pos.startContainer && pos.endContainer) {
        range.setStart(pos.startContainer, pos.startOffset);
        range.setEnd(pos.endContainer, pos.endOffset);
      } else {
        // Fallback: find position by character count
        let charIndex = 0;
        let found = false;

        const findNode = (node, targetIndex) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const length = node.textContent.length;
            if (charIndex + length >= targetIndex) {
              const offset = targetIndex - charIndex;
              range.setStart(node, Math.min(offset, length));
              range.setEnd(node, Math.min(offset, length));
              found = true;
              return true;
            }
            charIndex += length;
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            for (let child of node.childNodes) {
              if (findNode(child, targetIndex)) return true;
            }
          }
          return false;
        };

        if (!findNode(ref.current, pos.start || 0)) {
          // If we can't find the position, put cursor at end
          range.selectNodeContents(ref.current);
          range.collapse(false);
        }
      }

      selection.removeAllRanges();
      selection.addRange(range);
    } catch (err) {
      console.warn('Failed to restore cursor:', err);
    }
  };

  // Update content when value changes externally
  useEffect(() => {
    if (!ref.current) return;

    // Don't update if we're currently composing (IME input) or focused
    if (isComposingRef.current || ref.current.contains(document.activeElement)) {
      return;
    }

    const formattedHtml = formatText(value || '', format) || '';

    // Only update if content actually changed
    if (formattedHtml !== lastHtml.current) {
      const cursorPos = shouldUpdateRef.current ? saveCursorPosition() : null;
      ref.current.innerHTML = formattedHtml;
      lastHtml.current = formattedHtml;

      if (cursorPos) {
        requestAnimationFrame(() => {
          restoreCursorPosition(cursorPos);
        });
      }
    }
  }, [value, format]);

  const handleInput = useCallback((e) => {
    if (!ref.current || isComposingRef.current) return;

    const html = ref.current.innerHTML;
    if (html === lastHtml.current) return;

    lastHtml.current = html;

    // Extract text while explicitly preserving newlines from <br> tags
    // CRITICAL: Preserve all spaces and line breaks exactly as typed
    let text = '';
    const walker = document.createTreeWalker(
      ref.current,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      null,
      false
    );

    let node;
    while (node = walker.nextNode()) {
      if (node.nodeType === Node.TEXT_NODE) {
        // Preserve text content exactly as is
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
        // Add newline for <br> tags
        text += '\n';
      }
    }

    // Fallback to simple approach if tree walker didn't work
    if (!text && ref.current.innerHTML) {
      // This approach: replace <br> with newlines, then get text
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = ref.current.innerHTML.replace(/<br\s*\/?>/gi, '\n');
      text = tempDiv.textContent || '';
    }

    // Call onChange with the new text - preserve ALL spaces and newlines exactly
    if (onChange) {
      onChange(text);
    }
  }, [onChange]);

  // IME composition handling
  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
    shouldUpdateRef.current = false;
  }, []);

  const handleCompositionEnd = useCallback((e) => {
    isComposingRef.current = false;
    shouldUpdateRef.current = true;

    // Trigger update after IME finishes
    setTimeout(() => {
      if (ref.current) {
        const html = ref.current.innerHTML;
        // Extract text with newlines preserved
        let text = '';
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html.replace(/<br\s*\/?>/gi, '\n');
        text = tempDiv.textContent || '';
        
        lastHtml.current = html;

        if (onChange) {
          onChange(text);
        }
      }
    }, 0);
  }, [onChange]);

  const handleFocus = useCallback(() => {
    shouldUpdateRef.current = false;
  }, []);

  const handleBlurInternal = useCallback((e) => {
    shouldUpdateRef.current = true;
    if (onBlur) onBlur(e);
  }, [onBlur]);

  return (
    <div
      ref={ref}
      className={`${className} ${isEditing ? 'editing' : ''}`}
      contentEditable={!isReadOnly && isEditing}
      suppressContentEditableWarning
      onInput={handleInput}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onFocus={handleFocus}
      onBlur={handleBlurInternal}
      onKeyDown={onKeyDown}
      onClick={onClick}
      data-placeholder={placeholder}
      {...props}
      style={style}
    />
  );
});

const BlockComponent = ({
  block,
  isSelected,
  onUpdate,
  onDelete,
  onSelect,
  onAddBelow,
  onAddAbove,
  onMoveBlock,
  onDragStart,
  onDragEnd,
  onGetListNumber,
  placeholder = 'Type something...',
  isFirst,
  isLast,
  index,
  totalBlocks,
  dragOverIndex,
  isDragging,
  isReadOnly,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localContent, setLocalContent] = useState(block.properties?.title?.[0]?.[0] || '');
  const [caption, setCaption] = useState(block.properties?.caption || '');
  const [showBlockActions, setShowBlockActions] = useState(false);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [menuForceUpdate, setMenuForceUpdate] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  const contentRef = useRef(null);
  const dragHandleRef = useRef(null);
  const containerRef = useRef(null);
  const typeMenuRef = useRef(null);
  const updateTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Initialize content from block
  useEffect(() => {
    const newContent = block.properties?.title?.[0]?.[0] || '';
    setLocalContent(newContent);

    const newCaption = block.properties?.caption || '';
    setCaption(newCaption);

    // Initialize URLs from block properties
    if (block.type === BLOCK_TYPES.IMAGE) {
      const imgUrl = block.properties?.source?.[0]?.[0] || '';
      setImageUrl(imgUrl);
    }
    if (block.type === BLOCK_TYPES.FILE) {
      const fUrl = block.properties?.source?.[0]?.[0] || '';
      const fName = block.properties?.name || '';
      setFileUrl(fUrl);
      setFileName(fName);
    }
    if (block.type === BLOCK_TYPES.LINK) {
      const lUrl = block.properties?.link?.[0]?.[0] || '';
      const lText = block.properties?.text || '';
      setLinkUrl(lUrl);
      setLinkText(lText);
    }
  }, [block.id, block.properties]);

  // Close type menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTypeMenu &&
        typeMenuRef.current &&
        !typeMenuRef.current.contains(event.target) &&
        dragHandleRef.current &&
        !dragHandleRef.current.contains(event.target)) {
        setShowTypeMenu(false);
      }
    };

    if (showTypeMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showTypeMenu]);

  // Update parent when local content changes (with debounce)
  useEffect(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    if (localContent !== (block.properties?.title?.[0]?.[0] || '')) {
      updateTimeoutRef.current = setTimeout(() => {
        if (onUpdate) {
          // IMPORTANT: Preserve all spaces in localContent exactly as typed
          // DO NOT trim, strip, or modify the content in any way
          onUpdate({
            ...block,
            properties: {
              ...block.properties,
              title: [[localContent, block.properties?.title?.[0]?.[1] || []]]
            }
          });
        }
      }, 100);
    }

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [localContent, block, onUpdate]);

  // Drag functionality
  const [{ isDragging: isBlockDragging }, drag, preview] = useDrag(() => ({
    type: ItemTypes.BLOCK,
    item: () => {
      if (onDragStart) {
        onDragStart();
      }
      return {
        id: block.id,
        index,
        type: block.type
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (onDragEnd) {
        onDragEnd();
      }
    },
  }), [block.id, index, block.type, onDragStart, onDragEnd]);

  // Drop functionality
  const [{ handlerId, isOver }, drop] = useDrop({
    accept: ItemTypes.BLOCK,
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
    hover(item, monitor) {
      if (!containerRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = containerRef.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      if (onMoveBlock) {
        onMoveBlock(dragIndex, hoverIndex);
      }

      item.index = hoverIndex;
    },
  });

  // Connect drag and drop
  useEffect(() => {
    drop(containerRef);
    drag(dragHandleRef);
  }, [drag, drop]);

  // Auto-enter edit mode when selected
  useEffect(() => {
    if (!isReadOnly && isSelected && !isEditing) {
      setIsEditing(true);
    }
  }, [isReadOnly, isSelected, isEditing]);

  const handleContentChange = useCallback((newText) => {
    // IMPORTANT: Preserve all spaces - do NOT trim or modify content in any way
    // The text should be stored exactly as extracted from the contentEditable div
    setLocalContent(newText);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (isReadOnly) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setIsEditing(false);
      if (onAddBelow) {
        setTimeout(() => onAddBelow(), 10);
      }
    } else if (e.key === 'Backspace' && localContent === '') {
      e.preventDefault();
      if (onDelete) onDelete();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Handle tab for indentation if needed
    }
  }, [isReadOnly, localContent, onAddBelow, onDelete]);

  const handleBlur = useCallback(() => {
    if (!isReadOnly) {
      // Don't immediately exit edit mode on blur
    }
  }, [isReadOnly]);

  const handleCaptionChange = useCallback((e) => {
    // DO NOT trim captions - preserve all spaces exactly as entered
    const newCaption = e.target.value; // Keep exactly as is
    setCaption(newCaption);
    if (onUpdate) {
      onUpdate({
        ...block,
        properties: {
          ...block.properties,
          caption: newCaption
        }
      });
    }
  }, [block, onUpdate]);

  const handleToggleTodo = useCallback(() => {
    if (onUpdate) {
      onUpdate({
        ...block,
        properties: {
          ...block.properties,
          checked: !block.properties?.checked
        }
      });
    }
  }, [block, onUpdate]);

  const handleDragHandleClick = useCallback((e) => {
    e.stopPropagation();

    // Don't toggle if clicking inside the menu
    if (typeMenuRef.current && typeMenuRef.current.contains(e.target)) {
      return;
    }

    setShowTypeMenu(prev => !prev);
    if (onSelect) onSelect();
  }, [onSelect]);

  const handleSelectBlock = useCallback(() => {
    if (onSelect) onSelect();
    if (!isReadOnly) {
      setIsEditing(true);
    }
  }, [onSelect, isReadOnly]);

  // Handle block type change
  const handleBlockTypeChange = useCallback((newType) => {
    // Close menu immediately
    setShowTypeMenu(false);

    if (onUpdate) {
      // Preserve content when changing type
      const updatedBlock = {
        ...block,
        type: newType,
        properties: {
          ...block.properties,
          title: [[localContent, block.properties?.title?.[0]?.[1] || []]],
          // Reset specific properties based on type
          ...(newType === BLOCK_TYPES.TABLE && {
            rows: 3,
            cols: 3,
            data: Array(3).fill().map(() => Array(3).fill('')),
            columnWidths: {}
          }),
          ...(newType === BLOCK_TYPES.IMAGE && {
            source: [['', []]],
            caption: ''
          }),
          ...(newType === BLOCK_TYPES.FILE && {
            source: [['', []]],
            name: 'Untitled',
            size: 0
          }),
          ...(newType === BLOCK_TYPES.LINK && {
            link: [['', []]],
            text: localContent
          }),
          ...(newType === BLOCK_TYPES.CODE && {
            language: 'javascript'
          }),
          ...(newType === BLOCK_TYPES.CALLOUT && {
            icon: '💡'
          }),
          ...(newType === BLOCK_TYPES.TODO && {
            checked: false
          })
        }
      };

      // Remove properties that don't apply to the new type
      if (newType !== BLOCK_TYPES.TABLE) {
        delete updatedBlock.properties.rows;
        delete updatedBlock.properties.cols;
        delete updatedBlock.properties.data;
        delete updatedBlock.properties.columnWidths;
      }
      if (newType !== BLOCK_TYPES.IMAGE) {
        delete updatedBlock.properties.caption;
        delete updatedBlock.properties.source;
      }
      if (newType !== BLOCK_TYPES.FILE) {
        delete updatedBlock.properties.name;
        delete updatedBlock.properties.size;
        delete updatedBlock.properties.source;
      }
      if (newType !== BLOCK_TYPES.LINK) {
        delete updatedBlock.properties.link;
        delete updatedBlock.properties.text;
      }
      if (newType !== BLOCK_TYPES.CODE) {
        delete updatedBlock.properties.language;
      }
      if (newType !== BLOCK_TYPES.TODO) {
        delete updatedBlock.properties.checked;
      }

      // Force immediate update
      onUpdate(updatedBlock);
    }

    // Force menu re-render
    setMenuForceUpdate(prev => prev + 1);
  }, [block, localContent, onUpdate]);

  // File upload handler
  const handleFileUpload = async (event, type = 'file') => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadApi.uploadFile(file, type);
      const fileUrl = response.data.data.url;

      if (type === 'image') {
        // Update image block
        if (onUpdate) {
          onUpdate({
            ...block,
            properties: {
              ...block.properties,
              source: [[fileUrl, []]],
              caption: caption || file.name
            }
          });
        }
        setImageUrl(fileUrl);
      } else {
        // Update file block
        if (onUpdate) {
          onUpdate({
            ...block,
            properties: {
              ...block.properties,
              source: [[fileUrl, []]],
              name: file.name,
              size: file.size
            }
          });
        }
        setFileUrl(fileUrl);
        setFileName(file.name);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  // Image URL dialog handlers
  const handleImageUrlSubmit = () => {
    if (imageUrl.trim()) {
      let finalUrl = imageUrl.trim();
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        finalUrl = `https://${finalUrl}`;
      }

      if (onUpdate) {
        onUpdate({
          ...block,
          properties: {
            ...block.properties,
            source: [[finalUrl, []]]
          }
        });
      }
      setShowImageDialog(false);
    }
  };

  const handleImageUrlCancel = () => {
    setShowImageDialog(false);
    setImageUrl(block.properties?.source?.[0]?.[0] || '');
  };

  // File URL dialog handlers
  const handleFileUrlSubmit = () => {
    if (fileUrl.trim()) {
      let finalUrl = fileUrl.trim();
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        finalUrl = `https://${finalUrl}`;
      }

      if (onUpdate) {
        onUpdate({
          ...block,
          properties: {
            ...block.properties,
            source: [[finalUrl, []]],
            name: fileName || 'Download file'
          }
        });
      }
      setShowFileDialog(false);
    }
  };

  const handleFileUrlCancel = () => {
    setShowFileDialog(false);
    setFileUrl(block.properties?.source?.[0]?.[0] || '');
    setFileName(block.properties?.name || '');
  };

  // Link dialog handlers
  const handleLinkUrlSubmit = () => {
    if (linkUrl.trim()) {
      let finalUrl = linkUrl.trim();
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        finalUrl = `https://${finalUrl}`;
      }

      if (onUpdate) {
        onUpdate({
          ...block,
          properties: {
            ...block.properties,
            link: [[finalUrl, []]],
            text: linkText || finalUrl
          }
        });
      }
      setShowLinkDialog(false);
    }
  };

  const handleLinkUrlCancel = () => {
    setShowLinkDialog(false);
    setLinkUrl(block.properties?.link?.[0]?.[0] || '');
    setLinkText(block.properties?.text || '');
  };

  // Table editing state and handlers (keep existing table code)
  const [isEditingTable, setIsEditingTable] = useState(false);
  const [editableTableData, setEditableTableData] = useState([]);
  const [editableRows, setEditableRows] = useState(0);
  const [editableCols, setEditableCols] = useState(0);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  // Initialize table data
  useEffect(() => {
    if (block.type === BLOCK_TYPES.TABLE) {
      const rows = block.properties?.rows || 3;
      const cols = block.properties?.cols || 3;
      const tableData = block.properties?.data || Array(rows).fill().map(() => Array(cols).fill(''));
      const savedWidths = block.properties?.columnWidths || {};

      // Ensure tableData is properly formatted
      const formattedTableData = Array(rows).fill().map((_, rowIndex) =>
        Array(cols).fill().map((_, colIndex) =>
          tableData[rowIndex]?.[colIndex] || ''
        )
      );

      setEditableTableData(formattedTableData);
      setEditableRows(rows);
      setEditableCols(cols);
      setColumnWidths(savedWidths);
    }
  }, [block.properties, block.type, isEditingTable]);

  // Table editing handlers
  const handleTableEdit = useCallback(() => {
    if (isReadOnly) return;
    setIsEditingTable(true);
  }, [isReadOnly]);

  const handleTableSave = useCallback(() => {
    setIsEditingTable(false);
    if (onUpdate) {
      onUpdate({
        ...block,
        properties: {
          ...block.properties,
          data: editableTableData,
          rows: editableRows,
          cols: editableCols,
          columnWidths: columnWidths
        }
      });
    }
  }, [block, editableTableData, editableRows, editableCols, columnWidths, onUpdate]);

  const handleTableCancel = useCallback(() => {
    setIsEditingTable(false);
    const rows = block.properties?.rows || 3;
    const cols = block.properties?.cols || 3;
    const tableData = block.properties?.data || Array(rows).fill().map(() => Array(cols).fill(''));
    const savedWidths = block.properties?.columnWidths || {};

    // Ensure tableData is properly formatted
    const formattedTableData = Array(rows).fill().map((_, rowIndex) =>
      Array(cols).fill().map((_, colIndex) =>
        tableData[rowIndex]?.[colIndex] || ''
      )
    );

    setEditableTableData(formattedTableData);
    setEditableRows(rows);
    setEditableCols(cols);
    setColumnWidths(savedWidths);
  }, [block.properties]);

  const handleTableCellChange = useCallback((rowIndex, colIndex, value) => {
    setEditableTableData(prev => {
      const newData = [...prev];
      if (!newData[rowIndex]) newData[rowIndex] = [];
      newData[rowIndex][colIndex] = value;
      return newData;
    });
  }, []);

  const handleAddRow = useCallback(() => {
    setEditableRows(prev => prev + 1);
    setEditableTableData(prev => [...prev, Array(editableCols).fill('')]);
  }, [editableCols]);

  const handleAddColumn = useCallback(() => {
    const newColIndex = editableCols;
    setEditableCols(prev => prev + 1);
    setEditableTableData(prev => prev.map(row => {
      const newRow = [...row];
      newRow[newColIndex] = '';
      return newRow;
    }));

    // Initialize width for new column
    setColumnWidths(prev => ({
      ...prev,
      [newColIndex]: 120
    }));
  }, [editableCols]);

  const handleDeleteRow = useCallback((rowIndex) => {
    if (editableRows <= 1) return;
    setEditableRows(prev => prev - 1);
    setEditableTableData(prev => prev.filter((_, idx) => idx !== rowIndex));
  }, [editableRows]);

  const handleDeleteColumn = useCallback((colIndex) => {
    if (editableCols <= 1) return;
    setEditableCols(prev => prev - 1);
    setEditableTableData(prev => prev.map(row =>
      row.filter((_, idx) => idx !== colIndex)
    ));

    // Remove width for deleted column
    setColumnWidths(prev => {
      const newWidths = { ...prev };
      delete newWidths[colIndex];
      // Adjust indices for columns after deleted one
      const updatedWidths = {};
      Object.keys(newWidths).forEach(key => {
        const keyNum = parseInt(key);
        if (keyNum > colIndex) {
          updatedWidths[keyNum - 1] = newWidths[keyNum];
        } else {
          updatedWidths[keyNum] = newWidths[keyNum];
        }
      });
      return updatedWidths;
    });
  }, [editableCols]);

  // Column resize handlers
  const handleMouseMove = useCallback((e) => {
    if (resizingColumn === null) return;

    const deltaX = e.clientX - resizeStartX;
    const newWidth = Math.max(80, Math.min(500, resizeStartWidth + deltaX));

    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  const handleMouseUp = useCallback(() => {
    setResizingColumn(null);
  }, []);

  const handleResizeStart = useCallback((colIndex, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Get current width
    const width = columnWidths[colIndex] || 120;

    setResizingColumn(colIndex);
    setResizeStartX(e.clientX);
    setResizeStartWidth(width);
  }, [columnWidths]);

  // Add/remove global event listeners
  useEffect(() => {
    if (resizingColumn !== null) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [resizingColumn, handleMouseMove, handleMouseUp]);

  // Prevent text selection while resizing
  useEffect(() => {
    if (resizingColumn !== null) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      return () => {
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [resizingColumn]);

  // Block type options configuration
  const blockTypeOptions = [
    { type: BLOCK_TYPES.HEADER, label: 'Heading 1', icon: <FaHeading /> },
    { type: BLOCK_TYPES.SUB_HEADER, label: 'Heading 2', icon: <FaHeading /> },
    { type: BLOCK_TYPES.SUB_SUB_HEADER, label: 'Heading 3', icon: <FaHeading /> },
    { type: BLOCK_TYPES.TEXT, label: 'Text', icon: <FaRegSquare /> },
    { type: BLOCK_TYPES.CODE, label: 'Code', icon: <FaCode /> },
    { type: BLOCK_TYPES.QUOTE, label: 'Quote', icon: <FaQuoteRight /> },
    { type: BLOCK_TYPES.TODO, label: 'Todo', icon: <FaCheck /> },
    { type: BLOCK_TYPES.BULLETED_LIST, label: 'Bulleted List', icon: <FaListUl /> },
    { type: BLOCK_TYPES.NUMBERED_LIST, label: 'Numbered List', icon: <FaListOl /> },
    { type: BLOCK_TYPES.IMAGE, label: 'Image', icon: <FaImage /> },
    { type: BLOCK_TYPES.FILE, label: 'File', icon: <FaFile /> },
    { type: BLOCK_TYPES.LINK, label: 'Link', icon: <FaLink /> },
    { type: BLOCK_TYPES.DIVIDER, label: 'Divider', icon: <FaMinusIcon /> },
    { type: BLOCK_TYPES.CALLOUT, label: 'Callout', icon: <FaLightbulb /> },
    { type: BLOCK_TYPES.TABLE, label: 'Table', icon: <FaTable /> },
  ];

  const renderBlockContent = useCallback(() => {
    const format = block.properties?.title?.[0]?.[1] || [];

    switch (block.type) {
      case BLOCK_TYPES.HEADER:
        return (
          <StableContentEditable
            value={localContent}
            onChange={handleContentChange}
            placeholder="Heading 1"
            className="block-content block-h1"
            isEditing={!isReadOnly && isEditing}
            format={format}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={handleSelectBlock}
            isReadOnly={isReadOnly}
          />
        );

      case BLOCK_TYPES.SUB_HEADER:
        return (
          <StableContentEditable
            value={localContent}
            onChange={handleContentChange}
            placeholder="Heading 2"
            className="block-content block-h2"
            isEditing={!isReadOnly && isEditing}
            format={format}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={handleSelectBlock}
            isReadOnly={isReadOnly}
          />
        );

      case BLOCK_TYPES.SUB_SUB_HEADER:
        return (
          <StableContentEditable
            value={localContent}
            onChange={handleContentChange}
            placeholder="Heading 3"
            className="block-content block-h3"
            isEditing={!isReadOnly && isEditing}
            format={format}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={handleSelectBlock}
            isReadOnly={isReadOnly}
          />
        );

      case BLOCK_TYPES.TEXT:
        return (
          <StableContentEditable
            value={localContent}
            onChange={handleContentChange}
            placeholder={placeholder}
            className="block-content block-text"
            isEditing={!isReadOnly && isEditing}
            format={format}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={handleSelectBlock}
            isReadOnly={isReadOnly}
          />
        );

      case BLOCK_TYPES.CODE:
        return (
          <div className="code-block">
            <div className="code-header">
              <span className="code-language">{block.properties?.language || 'javascript'}</span>
            </div>
            <pre>
              <StableContentEditable
                style={{ color: "black" }}
                value={localContent}
                onChange={handleContentChange}
                placeholder="Enter code..."
                className="block-content block-code"
                isEditing={!isReadOnly && isEditing}
                format={[]}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onClick={handleSelectBlock}
                isReadOnly={isReadOnly}
              />
            </pre>
          </div>
        );

      case BLOCK_TYPES.QUOTE:
        return (
          <div className="quote-block">
            <div className="quote-decoration"></div>
            <StableContentEditable
              value={localContent}
              onChange={handleContentChange}
              placeholder="Enter quote..."
              className="block-content block-quote"
              isEditing={!isReadOnly && isEditing}
              format={format}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onClick={handleSelectBlock}
              isReadOnly={isReadOnly}
            />
          </div>
        );

      case BLOCK_TYPES.TODO:
        const isChecked = block.properties?.checked || false;
        return (
          <div className="todo-block">
            {!isReadOnly ? (
              <button
                className={`todo-checkbox ${isChecked ? 'checked' : ''}`}
                onClick={handleToggleTodo}
                type="button"
              >
                {isChecked && <FaCheck />}
              </button>
            ) : (
              <div className={`todo-checkbox ${isChecked ? 'checked' : ''}`}>
                {isChecked && <FaCheck />}
              </div>
            )}
            <StableContentEditable
              value={localContent}
              onChange={handleContentChange}
              placeholder="Todo item..."
              className={`block-content todo-text ${isChecked ? 'checked' : ''}`}
              isEditing={!isReadOnly && isEditing}
              format={format}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onClick={handleSelectBlock}
              isReadOnly={isReadOnly}
            />
          </div>
        );

      case BLOCK_TYPES.BULLETED_LIST:
        return (
          <div className="list-block">
            <div className="bullet-point"></div>
            <StableContentEditable
              value={localContent}
              onChange={handleContentChange}
              placeholder="List item..."
              className="block-content list-text"
              isEditing={!isReadOnly && isEditing}
              format={format}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onClick={handleSelectBlock}
              isReadOnly={isReadOnly}
            />
          </div>
        );

      case BLOCK_TYPES.NUMBERED_LIST:
        let listNumber = 1;
        if (typeof onGetListNumber === 'function') {
          listNumber = onGetListNumber();
        }

        return (
          <div className="list-block">
            <div className="number-point">
              {listNumber}.
            </div>
            <StableContentEditable
              value={localContent}
              onChange={handleContentChange}
              placeholder="List item..."
              className="block-content list-text"
              isEditing={!isReadOnly && isEditing}
              format={format}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onClick={handleSelectBlock}
              isReadOnly={isReadOnly}
            />
          </div>
        );

      case BLOCK_TYPES.IMAGE:
        const imageUrl = block.properties?.source?.[0]?.[0] || '';
        return (
          <div className="image-block">
            <div className="image-wrapper">
              <img
                src={imageUrl}
                alt={caption}
                className="image-preview"
              // onError={(e) => {
              //   e.target.style.display = 'none';
              //   e.target.parentElement.innerHTML = `
              //     <div class="image-error">
              //       <FaImage />
              //       <span>Failed to load image</span>
              //     </div>
              //   `;
              // }}
              />
            </div>

            {/* Image actions for non-readonly mode */}
            {!isReadOnly && (
              <div className="image-actions">
                <div className="image-caption-input">
                  <input
                    type="text"
                    value={caption}
                    onChange={handleCaptionChange}
                    placeholder="Add caption..."
                    className="image-caption"
                  />
                </div>
                <div className="image-source-actions">
                  <button
                    className="image-action-btn upload"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploading}
                    type="button"
                  >
                    <FaUpload /> Upload
                  </button>
                  {/* <button
                    className="image-action-btn url"
                    onClick={() => setShowImageDialog(true)}
                    type="button"
                  >
                    <FaGlobe /> URL
                  </button> */}
                  <input
                    type="file"
                    ref={imageInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'image')}
                    disabled={uploading}
                  />
                </div>
              </div>
            )}

            {isReadOnly && caption && (
              <div className="image-caption">
                {caption}
              </div>
            )}
          </div>
        );

      case BLOCK_TYPES.FILE:
        const fileUrl = block.properties?.source?.[0]?.[0] || '';
        const fileName = block.properties?.name || 'Download file';
        const fileSize = block.properties?.size || 0;

        return (
          <div className="file-block">
            <div className="file-icon-container">
              <FaFile className="file-icon" />
            </div>
            <div className="file-content">
              <div className="file-name">{fileName}</div>
              <div className="file-info">
                {fileSize > 0 && (
                  <span className="file-size">
                    {(fileSize / 1024).toFixed(1)} KB
                  </span>
                )}
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-download"
                >
                  Download
                </a>
              </div>
            </div>

            {/* File actions for non-readonly mode */}
            {!isReadOnly && (
              <div className="file-actions">
                <button
                  className="file-action-btn upload"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  type="button"
                >
                  <FaUpload /> Upload File
                </button>
                {/* <button
                  className="file-action-btn url"
                  onClick={() => setShowFileDialog(true)}
                  type="button"
                >
                  <FaGlobe /> URL
                </button> */}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileUpload(e, 'file')}
                  disabled={uploading}
                />
              </div>
            )}
          </div>
        );

      case BLOCK_TYPES.LINK:
        const linkUrl = block.properties?.link?.[0]?.[0] || '';
        const linkText = block.properties?.text || localContent || linkUrl;

        return (
          <div className="link-block">
            <div className="link-icon-container">
              <FaExternalLinkAlt className="link-icon" />
            </div>
            <a
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="link-content"
            >
              <span className="link-text">{linkText}</span>
              <span className="link-url">{linkUrl}</span>
            </a>

            {/* Link actions for non-readonly mode */}
            {!isReadOnly && (
              <div className="link-actions">
                <button
                  className="link-action-btn edit"
                  onClick={() => setShowLinkDialog(true)}
                  type="button"
                >
                  <FaEdit /> Edit Link
                </button>
              </div>
            )}
          </div>
        );

      case BLOCK_TYPES.DIVIDER:
        return <hr className="block-divider" />;

      case BLOCK_TYPES.CALLOUT:
        const calloutIcon = block.properties?.icon || '💡';
        return (
          <div className="callout-block">
            <div className="callout-icon">{calloutIcon}</div>
            <StableContentEditable
              value={localContent}
              onChange={handleContentChange}
              placeholder="Callout text..."
              className="block-content callout-text"
              isEditing={!isReadOnly && isEditing}
              format={format}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onClick={handleSelectBlock}
              isReadOnly={isReadOnly}
            />
          </div>
        );

      case BLOCK_TYPES.TABLE:
        const rows = block.properties?.rows || 3;
        const cols = block.properties?.cols || 3;
        const tableData = block.properties?.data || Array(rows).fill().map(() => Array(cols).fill(''));
        const savedColumnWidths = block.properties?.columnWidths || {};

        // Helper function to convert URLs to "doc link" text with clickable link
        const convertUrlsToDocLinks = (text) => {
          if (!text) return text;

          // URL regex pattern
          const urlPattern = /(https?:\/\/[^\s]+)/g;

          // Check if text contains URLs
          if (!urlPattern.test(text)) {
            return text;
          }

          // Replace URLs with "doc link" text that links to the URL
          return text.replace(urlPattern, (url) => {
            return `<a href="${url}" target="_blank" rel="noopener noreferrer" 
              class="doc-link" style="color: #2563eb; text-decoration: underline; cursor: pointer;">
              Doc Link
              </a>`;
          });
        };

        // Helper to extract plain text from HTML content
        const extractText = (htmlContent) => {
          if (!htmlContent) return '';

          // Create a temporary element to parse HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlContent;

          // Get text content, preserving line breaks
          let text = tempDiv.textContent || tempDiv.innerText || '';

          // Remove any remaining HTML tags
          text = text.replace(/<[^>]*>/g, '');

          return text;
        };

        if (!isReadOnly && isEditingTable) {
          return (
            <div className="table-block editing">
              <div className="table-header">
                <div className="table-title">
                  <FaTable />
                  <span>Edit Table ({editableRows} × {editableCols})</span>
                </div>
                <div className="table-tools">
                  <button className="table-tool-btn" onClick={handleAddRow} type="button">
                    <FaPlus /> Add Row
                  </button>
                  <button className="table-tool-btn" onClick={handleAddColumn} type="button">
                    <FaPlus /> Add Column
                  </button>
                </div>
              </div>

              <div className="editable-table-container">
                <table className="editable-table">
                  <thead>
                    <tr>
                      <th style={{ width: '100px', minWidth: '100px' }}></th>
                      {Array.from({ length: editableCols }).map((_, colIndex) => {
                        const width = columnWidths[colIndex] || 120;
                        return (
                          <th
                            key={colIndex}
                            className="column-header"
                            style={{
                              width: `${width}px`,
                              position: 'relative',
                              minWidth: '80px',
                              maxWidth: '500px',
                              backgroundColor: resizingColumn === colIndex ? 'rgba(0, 123, 255, 0.1)' : 'transparent'
                            }}
                          >
                            <div className="column-actions">
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '4px 0'
                              }}>
                                <span style={{ fontSize: '12px', fontWeight: '500' }}>
                                  Column {colIndex + 1}
                                </span>
                                <button
                                  className="table-action-btn delete"
                                  onClick={() => handleDeleteColumn(colIndex)}
                                  disabled={editableCols <= 1}
                                  title="Delete Column"
                                  type="button"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </div>
                            {/* Resize Handle */}
                            <div
                              className="table-resize-handle"
                              onMouseDown={(e) => handleResizeStart(colIndex, e)}
                              title="Drag to resize column"
                              style={{
                                position: 'absolute',
                                top: 0,
                                right: "-2px",
                                width: "6px",
                                height: '100%',
                                cursor: 'col-resize',
                                zIndex: 10,
                                backgroundColor: resizingColumn === colIndex ? 'red' : 'transparent',
                                opacity: resizingColumn === colIndex ? 0.7 : 0,
                                transition: 'opacity 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (resizingColumn === null) {
                                  e.target.style.opacity = '0.5';
                                  e.target.style.backgroundColor = 'red';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (resizingColumn !== colIndex) {
                                  e.target.style.opacity = '0';
                                  e.target.style.backgroundColor = 'transparent';
                                }
                              }}
                            />
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: editableRows }).map((_, rowIndex) => (
                      <tr key={rowIndex}>
                        <td className="row-header" style={{ width: '100px', minWidth: '100px' }}>
                          <div className="row-actions">
                            <span style={{ fontSize: '12px', fontWeight: '500' }}>
                              {rowIndex === 0 ? 'Header' : `Row ${rowIndex + 1}`}
                            </span>
                            <button
                              className="table-action-btn delete"
                              onClick={() => handleDeleteRow(rowIndex)}
                              disabled={editableRows <= 1}
                              title="Delete Row"
                              type="button"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                        {Array.from({ length: editableCols }).map((_, colIndex) => {
                          const width = columnWidths[colIndex] || 120;
                          const cellContent = editableTableData[rowIndex]?.[colIndex] || '';

                          return (
                            <td
                              key={colIndex}
                              className="editable-cell"
                              style={{
                                width: `${width}px`,
                                minWidth: `${width}px`,
                                maxWidth: `${width}px`,
                                position: 'relative'
                              }}
                            >
                              <div
                                className="table-cell-input"
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                  const textContent = e.target.textContent || '';
                                  handleTableCellChange(rowIndex, colIndex, textContent);
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: cellContent
                                }}
                                style={{
                                  minHeight: '40px',
                                  outline: 'none',
                                  padding: '6px',
                                  borderRadius: '4px',
                                  transition: 'all 0.2s',
                                  fontFamily: "'Inter', sans-serif",
                                  fontSize: '13px',
                                  width: '100%',
                                  boxSizing: 'border-box',
                                }}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="table-actions">
                <button className="table-btn save" onClick={handleTableSave} type="button">
                  <FaSave /> Save Table
                </button>
                <button className="table-btn cancel" onClick={handleTableCancel} type="button">
                  <FaTimes /> Cancel
                </button>
              </div>
            </div>
          );
        }

        // View mode - Render table with "doc link" text for URLs
        return (
          <div className="table-block">
            <div className="table-header">
              {!isReadOnly && (
                <button className="table-edit-btn" onClick={handleTableEdit} title="Edit Table" type="button">
                  <FaEdit />
                </button>
              )}
            </div>

            <div className="table-container">
              <table className="block-table">
                <tbody>
                  {Array.from({ length: rows }).map((_, rowIndex) => (
                    <tr key={rowIndex}>
                      {Array.from({ length: cols }).map((_, colIndex) => {
                        const width = savedColumnWidths[colIndex];
                        const cellContent = tableData[rowIndex]?.[colIndex] || '';
                        const contentWithDocLinks = convertUrlsToDocLinks(cellContent);

                        return (
                          <td
                            key={colIndex}
                            style={width ? {
                              width: `${width}px`,
                              minWidth: `${width}px`,
                              maxWidth: `${width}px`
                            } : {}}
                          >
                            <div
                              className="table-cell"
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                minHeight: '40px',
                                padding: '4px',
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '13px'
                              }}
                              dangerouslySetInnerHTML={{ __html: contentWithDocLinks }}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!isReadOnly && (
              <div className="table-footer">
                <span className="table-hint">Click the edit button to modify table (drag column edges to resize)</span>
              </div>
            )}
          </div>
        );

      default:
        return (
          <StableContentEditable
            value={localContent}
            onChange={handleContentChange}
            placeholder={placeholder}
            className="block-content block-text"
            isEditing={!isReadOnly && isEditing}
            format={format}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={handleSelectBlock}
            isReadOnly={isReadOnly}
          />
        );
    }
  }, [
    block, localContent, caption, isReadOnly, isEditing, placeholder,
    handleContentChange, handleKeyDown, handleBlur, handleSelectBlock,
    handleToggleTodo, handleCaptionChange, handleTableEdit, handleTableSave,
    handleTableCancel, handleAddRow, handleAddColumn, handleDeleteRow,
    handleDeleteColumn, handleTableCellChange, isEditingTable,
    editableTableData, editableRows, editableCols, columnWidths,
    resizingColumn, handleResizeStart, onGetListNumber, menuForceUpdate,
    uploading, handleFileUpload, imageUrl, fileUrl, fileName, linkUrl, linkText
  ]);

  const showDragIndicator = isOver && !isBlockDragging && !isReadOnly;

  return (
    <div
      ref={containerRef}
      className={`block-container ${isSelected ? 'selected' : ''} ${isBlockDragging ? 'dragging' : ''} ${showDragIndicator ? 'drag-over' : ''} ${isReadOnly ? 'read-only' : ''}`}
      style={{ opacity: isBlockDragging ? 0.5 : 1 }}
      data-block-id={block.id}
      data-handler-id={handlerId}
      data-index={index}
      onMouseEnter={() => setShowBlockActions(true)}
      onMouseLeave={() => setShowBlockActions(false)}
    >
      {!isReadOnly && (
        <div
          ref={dragHandleRef}
          className={`block-drag-handle ${showTypeMenu ? 'active' : ''}`}
          title="Double-click to change type, drag to reorder"
          onClick={(e) => {
            e.stopPropagation();
            // Single click: only select the block
            if (onSelect) onSelect();

            // Don't enter edit mode on single click of drag handle
            if (!isReadOnly) {
              setIsEditing(false);
            }
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            e.preventDefault();

            // Double click: open type menu
            setShowTypeMenu(prev => !prev);

            // Ensure block is selected when opening menu
            if (onSelect) onSelect();
          }}
          // onMouseEnter={(e) => {
          //   // Add tooltip on hover
          //   const tooltip = document.createElement('div');
          //   tooltip.className = 'drag-handle-tooltip';
          //   tooltip.textContent = 'Double-click to change type';
          //   tooltip.style.position = 'absolute';
          //   tooltip.style.top = '-30px';
          //   tooltip.style.left = '50%';
          //   tooltip.style.transform = 'translateX(-50%)';
          //   tooltip.style.backgroundColor = 'rgba(0,0,0,0.8)';
          //   tooltip.style.color = 'white';
          //   tooltip.style.padding = '4px 8px';
          //   tooltip.style.borderRadius = '4px';
          //   tooltip.style.fontSize = '12px';
          //   tooltip.style.whiteSpace = 'nowrap';
          //   tooltip.style.zIndex = '1000';
          //   tooltip.style.pointerEvents = 'none';

          //   e.currentTarget.appendChild(tooltip);
          //   e.currentTarget.dataset.hasTooltip = 'true';
          // }}
          // onMouseLeave={(e) => {
          //   // Remove tooltip
          //   const tooltip = e.currentTarget.querySelector('.drag-handle-tooltip');
          //   if (tooltip) {
          //     tooltip.remove();
          //   }
          //   delete e.currentTarget.dataset.hasTooltip;
          // }}
        >
          <FaGripVertical />

          {showTypeMenu && (
            <div
              ref={typeMenuRef}
              className="block-type-menu"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="block-type-header">
                <span>Change Block Type</span>
                <button
                  className="close-type-menu"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTypeMenu(false);
                  }}
                  type="button"
                >
                  <FaTimes />
                </button>
              </div>
              <div className="block-type-list">
                {blockTypeOptions.map((option) => (
                  <button
                    key={`${option.type}-${menuForceUpdate}`}
                    className={`block-type-option ${block.type === option.type ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBlockTypeChange(option.type);
                    }}
                    type="button"
                  >
                    <span className="block-type-icon">{option.icon}</span>
                    <span className="block-type-label">{option.label}</span>
                    {block.type === option.type && (
                      <span className="block-type-check"><FaCheck /></span>
                    )}
                  </button>
                ))}
              </div>
              <div className="block-type-footer">
                Current: {blockTypeOptions.find(opt => opt.type === block.type)?.label || block.type}
              </div>
            </div>
          )}
        </div>
      )}

      {renderBlockContent()}

      {/* Dialogs for URL input */}
      {showImageDialog && (
        <div className="url-dialog-overlay">
          <div className="url-dialog">
            <h3>Set Image URL</h3>
            <div className="url-input-group">
              <label>Image URL:</label>
              <input
                type="text"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                autoFocus
              />
            </div>
            <div className="url-dialog-actions">
              <button
                className="url-btn-cancel"
                onClick={handleImageUrlCancel}
              >
                Cancel
              </button>
              <button
                className="url-btn-submit"
                onClick={handleImageUrlSubmit}
                disabled={!imageUrl.trim()}
              >
                Set URL
              </button>
            </div>
          </div>
        </div>
      )}

      {showFileDialog && (
        <div className="url-dialog-overlay">
          <div className="url-dialog">
            <h3>Set File URL</h3>
            <div className="url-input-group">
              <label>File URL:</label>
              <input
                type="text"
                value={fileUrl}
                onChange={(e) => setFileUrl(e.target.value)}
                placeholder="https://example.com/file.pdf"
                autoFocus
              />
            </div>
            <div className="url-input-group">
              <label>File Name (optional):</label>
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Document.pdf"
              />
            </div>
            <div className="url-dialog-actions">
              <button
                className="url-btn-cancel"
                onClick={handleFileUrlCancel}
              >
                Cancel
              </button>
              <button
                className="url-btn-submit"
                onClick={handleFileUrlSubmit}
                disabled={!fileUrl.trim()}
              >
                Set URL
              </button>
            </div>
          </div>
        </div>
      )}

      {showLinkDialog && (
        <div className="url-dialog-overlay">
          <div className="url-dialog">
            <h3>Edit Link</h3>
            <div className="url-input-group">
              <label>URL:</label>
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                autoFocus
              />
            </div>
            <div className="url-input-group">
              <label>Text (optional):</label>
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Link text"
              />
            </div>
            <div className="url-dialog-actions">
              <button
                className="url-btn-cancel"
                onClick={handleLinkUrlCancel}
              >
                Cancel
              </button>
              <button
                className="url-btn-submit"
                onClick={handleLinkUrlSubmit}
                disabled={!linkUrl.trim()}
              >
                Update Link
              </button>
            </div>
          </div>
        </div>
      )}

      {showDragIndicator && (
        <div className="drag-indicator">
          <div className="drag-line"></div>
          <div className="drag-circle"></div>
        </div>
      )}

      {!isReadOnly && (isSelected || showBlockActions) && (
        <div className="block-actions">
          <button onClick={onDelete} className="delete-btn" title="Delete" type="button">
            <FaTrash />
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(BlockComponent, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  // Include block.type in comparison to ensure re-render when type changes
  const blockChanged =
    prevProps.block.type !== nextProps.block.type ||
    JSON.stringify(prevProps.block.properties) !== JSON.stringify(nextProps.block.properties);

  return !blockChanged &&
    prevProps.block.id === nextProps.block.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isReadOnly === nextProps.isReadOnly &&
    prevProps.index === nextProps.index &&
    prevProps.dragOverIndex === nextProps.dragOverIndex &&
    prevProps.isDragging === nextProps.isDragging;
});